<template>
  <div class="video-player-fi">
    <template v-if="!isEditMode">
      <div
        class="video-player-fi__preview"
        :style="aspectRatio ? `aspect-ratio: ${aspectRatio}` : null"
      >
        <video-player-active-fi
          v-if="isActivated"
          ref="activePlayer"
          :fields="fields"
          :show-hide-delay="showHideDelay"
          :modal-delay="modalDelay"
          :no-youku-player="noYoukuPlayer"
          :is-thumbnail="isThumbnail"
          :reset-video="resetVideo"
          :pause-video="pauseVideo"
          @video="emit('video', $event)"
          @update:play-count="playCount = $event"
        >
          <template
            v-if="slots.responsiveVideoSource"
            #responsiveVideoSource
          >
            <slot name="responsiveVideoSource" />
          </template>
        </video-player-active-fi>

        <div class="video-player-fi__overlay">
          <div
            :class="{
              'video-player-fi__play-button': true,
              'video-player-fi__play-button--small': smallPlayButton,
            }"
            @click="onPlayButtonClicked"
          >
            <icon-fi
              v-if="playCount === 0"
              icon="play"
            />

            <icon-fi
              v-else
              icon="repeat"
            />
          </div>
        </div>

        <slot
          v-if="slots.previewImage"
          name="previewImage"
        />
        <template v-else-if="overlayBreakpointsDesktop">
          <ssr-cloud-image-fi
            class="video-player-fi__image fi-hidden lg:fi-inline-block"
            :breakpoints="overlayBreakpointsDesktop ?? []"
            :unlimited="unlimited"
            :aspect-ratio="aspectRatio"
            :field="fields.image || undefined"
            :alt="fields?.imageAlt?.value"
            :title="fields?.imageTitle?.value"
          />
          <ssr-cloud-image-fi
            class="video-player-fi__image fi-hidden md:fi-inline-block lg:fi-hidden"
            :breakpoints="overlayBreakpointsTablet ?? []"
            :unlimited="unlimitedTablet"
            :aspect-ratio="aspectRatio"
            :field="fields.imageTablet || fields.image || undefined"
            :alt="fields?.imageAlt?.value"
            :title="fields?.imageTitle?.value"
          />
          <ssr-cloud-image-fi
            class="video-player-fi__image fi-inline-block md:fi-hidden"
            :breakpoints="overlayBreakpointsMobile ?? []"
            :unlimited="unlimitedMobile"
            :aspect-ratio="aspectRatio"
            :field="fields.imageMobile || fields.image || undefined"
            :alt="fields?.imageAlt?.value"
            :title="fields?.imageTitle?.value"
          />
        </template>
        <template v-else-if="!isMounted">
          <cloud-image-fi
            class="video-player-fi__image fi-hidden lg:fi-inline-block"
            :field="fields.image || undefined"
            :alt="fields?.imageAlt?.value"
            :title="fields?.imageTitle?.value"
            :aspect-ratio="aspectRatio"
          />
          <cloud-image-fi
            class="video-player-fi__image fi-hidden md:fi-inline-block lg:fi-hidden"
            :field="fields.imageTablet || fields.image || undefined"
            :alt="fields?.imageAlt?.value"
            :title="fields?.imageTitle?.value"
            :aspect-ratio="aspectRatio"
          />
          <cloud-image-fi
            class="video-player-fi__image fi-inline-block md:fi-hidden"
            :field="fields.imageMobile || fields.image || undefined"
            :alt="fields?.imageAlt?.value"
            :title="fields?.imageTitle?.value"
            :aspect-ratio="aspectRatio"
          />
        </template>
        <cloud-image-fi
          v-else
          class="video-player-fi__image"
          :src="getImage"
          :alt="fields?.imageAlt?.value"
          :title="fields?.imageTitle?.value"
          :params="previewParams ? previewParams : 'org_if_sml=0'"
          :sizes="overlaySizes"
          :do-not-replace-url="doNotReplaceUrl"
          :aspect-ratio="aspectRatio"
        />
      </div>

      <div
        v-if="videoTag || videoHeadline"
        class="video-player-fi__text"
      >
        <div
          v-if="videoTag"
          class="video-player-fi__badge badge-fi"
        >
          {{ videoTag }}
        </div>
        <div
          v-if="videoHeadline"
          class="video-player-fi__headline"
        >
          {{ videoHeadline }}
        </div>
      </div>
    </template>
    <template v-else>
      <div
        class="video-player-fi__preview"
        :style="aspectRatio ? `aspect-ratio: ${aspectRatio}` : null"
      >
        <div class="video-player-fi__overlay video-player-fi__overlay--xpe">
          <div class="video-player-fi__play-button">
            <icon-fi
              v-if="playCount === 0"
              icon="play"
            />

            <icon-fi
              v-else
              icon="repeat"
            />
          </div>
        </div>

        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="fields.videoTag || fields.videoHeadline"
          class="video-player-fi__text"
        >
          <div
            class="video-player-fi__badge badge-fi"
            v-html="fields.videoTag?.editable"
          />
          <div
            class="video-player-fi__headline"
            v-html="fields.videoHeadline?.editable"
          />
        </div>

        <div class="video-player-fi__image video-player-fi__image--xpe">
          <div v-html="fields.image?.editable" />
        </div>
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { CloudImageFi, IconFi, SsrCloudImageFi } from 'atoms';
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  ref,
  useSlots,
  useTemplateRef,
} from 'vue';

import { useBreakpoints } from '@/composables/breakpoint';
import screenWidths from '@/tailwind/screenWidths';

import type { OverlayBreakpoint, VideoPlayerFiFields } from './types';

defineOptions({ name: 'VideoPlayerFi' });

const VideoPlayerActiveFi = defineAsyncComponent(() => import('./VideoPlayerActiveFi.vue'));

interface Props {
  fields: VideoPlayerFiFields;
  previewParams?: string | null;
  showHideDelay?: number;
  modalDelay?: number;
  resetVideo?: boolean;
  pauseVideo?: boolean;
  overlaySizes?: object;
  overlayBreakpoints?: OverlayBreakpoint[];
  unlimited?: number;
  doNotReplaceUrl?: boolean;
  aspectRatio?: number;
  smallPlayButton?: boolean;
  noYoukuPlayer?: boolean;
  isThumbnail?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  previewParams: null,
  showHideDelay: 500,
  modalDelay: 1000,
  resetVideo: false,
  pauseVideo: false,
  overlaySizes: undefined,
  overlayBreakpoints: undefined,
  unlimited: undefined,
  doNotReplaceUrl: false,
  aspectRatio: undefined,
  smallPlayButton: false,
  noYoukuPlayer: false,
  isThumbnail: false,
});

const emit = defineEmits<{
  video: [payload: string];
}>();

const slots = useSlots();
const { isSM, isMD } = useBreakpoints();

const activePlayer = useTemplateRef('activePlayer');

const isActivated = ref(false);
const playCount = ref(0);
const isMounted = ref(false);

const unlimitedMobile = ref<number>();
const unlimitedTablet = ref<number>();
const overlayBreakpointsDesktop = ref<OverlayBreakpoint[]>();
const overlayBreakpointsTablet = ref<OverlayBreakpoint[]>();
const overlayBreakpointsMobile = ref<OverlayBreakpoint[]>();

const videoAutoPlay = computed(() => props.fields?.autoplay?.value);

const videoTag = computed(() => props.fields?.videoTag?.value);

const videoHeadline = computed(() => props.fields?.videoHeadline?.value);

const getImage = computed(() => {
  // image desktop
  if (isMD.value && props.fields.image?.value?.src) {
    return props.fields.image.value.src;
  }
  // image tablet
  if (isSM.value && props.fields.imageTablet?.value?.src) {
    return props.fields.imageTablet.value.src;
  }
  // image mobile
  if (props.fields.imageMobile?.value?.src) {
    return props.fields.imageMobile.value.src;
  }
  // image fallback to desktop if no tablet or mobile
  if (props.fields.image?.value?.src) {
    return props.fields.image.value.src;
  }
  // if no image data
  return undefined;
});

const isPlaying = computed(() => activePlayer.value?.isPlaying ?? false);

const videoEnded = () => {
  activePlayer.value?.videoEnded();
};

// created() logic — runs at setup time
if (props.overlayBreakpoints?.length) {
  const sortedBreakpoints = [...props.overlayBreakpoints].sort(
    (a, b) => a.mediaWidth - b.mediaWidth,
  );

  const overlayBreakpointsMobileValue: OverlayBreakpoint[] = [];
  overlayBreakpointsMobile.value = overlayBreakpointsMobileValue;

  const overlayBreakpointsTabletValue: OverlayBreakpoint[] = [];
  overlayBreakpointsTablet.value = overlayBreakpointsTabletValue;

  const overlayBreakpointsDesktopValue: OverlayBreakpoint[] = [];
  overlayBreakpointsDesktop.value = overlayBreakpointsDesktopValue;

  sortedBreakpoints.forEach((breakpoint) => {
    if (breakpoint.mediaWidth < screenWidths.sm) {
      overlayBreakpointsMobileValue.push(breakpoint);
      return;
    }
    if (breakpoint.mediaWidth < screenWidths.md) {
      overlayBreakpointsTabletValue.push(breakpoint);
      return;
    }
    overlayBreakpointsDesktopValue.push(breakpoint);
  });

  if (overlayBreakpointsMobileValue.length) {
    unlimitedMobile.value = overlayBreakpointsMobileValue.pop()?.mediaWidth;
  } else {
    unlimitedMobile.value =
      overlayBreakpointsTabletValue[0]?.mediaWidth ||
      overlayBreakpointsDesktopValue[0]?.mediaWidth ||
      props.unlimited;
  }

  if (overlayBreakpointsTabletValue.length) {
    unlimitedTablet.value = overlayBreakpointsTabletValue.pop()?.mediaWidth;
  } else {
    unlimitedTablet.value = overlayBreakpointsDesktopValue[0]?.mediaWidth || props.unlimited;
  }
}

onMounted(() => {
  isMounted.value = true;
  if (videoAutoPlay.value) {
    isActivated.value = true;
  }
});

const onPlayButtonClicked = () => {
  if (isActivated.value) {
    // Already activated — delegate to active player (e.g. repeat)
    nextTick(() => {
      activePlayer.value?.playButtonClicked();
    });
  } else {
    isActivated.value = true;
    // Active player calls playButtonClicked() on mount
  }
};

defineExpose({ isPlaying, videoEnded });
</script>

<style scoped src="./video-player-fi" />
